import React, {
  useContext,
  useEffect,
  useRef,
  ReactNode,
  PropsWithChildren,
} from "react"
import styles from "./BigChip.module.sass"
import { hexToRgbArray } from "../../../lib/helpers"
import TradeInThemeContext from "../TradeInWizard/contexts/theme"
import { ChoiceValue } from "./carAssemblageTypes"

interface BigChipProps {
  children?: ReactNode
  label: string
  value: ChoiceValue
  isSelected: boolean
  onSelect: (value: ChoiceValue) => void
  description?: string
  shortDescription?: string
  withCheckbox?: boolean
}

// The component should always have a label passed
// If no description is passed, the label will be rendered styled as description
const BigChip = ({
  isSelected,
  onSelect,
  label,
  value,
  description,
  shortDescription,
  withCheckbox,
  children,
}: PropsWithChildren<BigChipProps>) => {
  const theme = useContext(TradeInThemeContext)

  const chipRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (chipRef.current) {
      chipRef.current.style.setProperty(
        "--accent-color-rgb",
        hexToRgbArray(theme.color).join(", ")
      )
    }
  }, [theme])

  return (
    <span
      ref={chipRef}
      className={[
        styles.bigChip,
        isSelected && styles.selected,
        withCheckbox && styles.withCheckbox,
      ]
        .filter(Boolean)
        .join(" ")}
      onClick={() => onSelect(value)}
      onKeyDown={(event) => {
        if (event.key === " " || event.key === "Enter") {
          onSelect(value)
        }
      }}
      role="button"
      aria-pressed={isSelected}
      tabIndex={0}
    >
      <span>
        {label &&
          (description || shortDescription ? (
            <strong>{label}</strong>
          ) : (
            <p>{label}</p>
          ))}
        {shortDescription && !isSelected && (
          <span className={styles.shortDescription}>{shortDescription}</span>
        )}
      </span>

      {/* When shortDescription is available, it should show next to the label.
      The full description should only be visible when the option is selected */}
      {description ? (
        shortDescription ? (
          isSelected && <span className={styles.checkbox}>{description}</span>
        ) : (
          <span className={styles.description}>{description}</span>
        )
      ) : null}
      {isSelected && children}
    </span>
  )
}

export default BigChip
